/**
 * Reusable Yup Validation Schemas
 * 
 * Common validation schemas that can be composed into forms.
 * 
 * i18n: Configure yup locale once at app startup using configureYupLocale()
 * from './locale'. All schemas will automatically use translated messages.
 */

import * as yup from 'yup';
import { CountryCode } from 'libphonenumber-js';
import { validatePhoneNumber } from './phone-validation';

const TLD_PATTERN = /\.[a-zA-Z]{2,}$/;

/**
 * Email field schema
 * 
 * Uses yup's built-in email validation plus a TLD check
 * to reject bare domains like "f@f".
 * 
 * @param invalidMessage - Override for the invalid-email error.
 *   When omitted, falls through to the yup locale set by configureYupLocale().
 * 
 * @example
 * emailSchema()                                      // uses yup locale
 * emailSchema(t('error_email_invalid'))              // field-specific message
 */
export function emailSchema(invalidMessage?: string) {
  const base = invalidMessage
    ? yup.string().email(invalidMessage).matches(TLD_PATTERN, { excludeEmptyString: true, message: invalidMessage })
    : yup.string().email().matches(TLD_PATTERN, { excludeEmptyString: true });
  return base.required(invalidMessage);
}

/**
 * Optional email field schema
 * 
 * @param invalidMessage - Override for the invalid-email error.
 */
export function emailSchemaOptional(invalidMessage?: string) {
  return invalidMessage
    ? yup.string().email(invalidMessage).matches(TLD_PATTERN, { excludeEmptyString: true, message: invalidMessage })
    : yup.string().email().matches(TLD_PATTERN, { excludeEmptyString: true });
}

/**
 * Phone number field schema
 *
 * @param country - ISO country code (default: 'US')
 * @param required - Whether the field is required (default: true)
 * @param invalidMessage - Translated error message for invalid phone numbers.
 *   Must be provided by the caller because yup.test() does not go through the
 *   yup locale system — passing a raw i18n key here will render as-is.
 *
 * @example
 * phoneSchema()                                    // US, required, default message
 * phoneSchema('CA')                                // Canadian, required
 * phoneSchema('CA', true, t('error_phone_invalid')) // with translated message
 */
export function phoneSchema(
  country: CountryCode = 'US',
  required = true,
  invalidMessage = 'Please enter a valid phone number',
) {
  const schema = yup
    .string()
    .test('phone', invalidMessage, value => !value || validatePhoneNumber(value, country));

  return required ? schema.required(invalidMessage) : schema;
}

/**
 * Required text field schema
 * 
 * @param requiredMessage - Override for the required error.
 *   When omitted, falls through to the yup locale set by configureYupLocale().
 * 
 * @example
 * requiredTextSchema()                               // uses yup locale
 * requiredTextSchema(t('error_first_name_required'))  // field-specific message
 */
export function requiredTextSchema(requiredMessage?: string) {
  return requiredMessage ? yup.string().required(requiredMessage) : yup.string().required();
}

/**
 * Optional text field schema
 * 
 * @example
 * optionalTextSchema()
 * optionalTextSchema().min(10).max(500)
 */
export function optionalTextSchema() {
  return yup.string();
}

/**
 * Required checkbox schema (must be checked)
 * 
 * Uses yup locale for error messages. Configure with configureYupLocale().
 * 
 * @example
 * requiredCheckboxSchema() // Uses yup locale
 */
export function requiredCheckboxSchema() {
  return yup.boolean().oneOf([true]);
}
